// GENERATED — do not edit by hand.
//
// Consonant sound values for the Hismaic letters, one entry per vocabulary class.
//
// THE SCRIPT RECORDS NO VOWELS. Every value here is a consonant; the generator rejects
// anything else. Vowels are a separate and weaker claim and do not live in this file.
//
// `certainty` is the whole point of the table:
//   'secure'    — the value is not in dispute
//   'contested' — rival readings exist and are listed in `alternatives`
//   'unknown'   — no value is recorded here (illegible and unknown are never spoken)
//
// `render` records what the synthesizer was told to say and what it actually produced.
// Where they differ the audio is an approximation and the UI must say so.
//
// NOTHING IN THIS TABLE HAS BEEN REVIEWED BY AN EPIGRAPHER — every `reviewedBy` is null.
// The values are drafted from the published literature and are provisional.
//
// Source: phonology/sound_values.json + phonology/sources.json
// Regenerate with:  uv run python -m hismaic.corpus.phonology --write
// vocab  sha256: 427aa136e90c4f58ef0c93a46eedd035177d92cb11b242c44dcc47ad864faae0
// values sha256: a38f1a373d89db52d704a03714e9a4814a56114661131ed9901f7782b12c600f

export interface Citation {
  /** Key into REFERENCES. */
  key: string;
  /** Page or section, or null when only the work is cited and no page was verified. */
  locus: string | null;
}

export interface Alternative {
  /** A rival reading, IPA. */
  ipa: string;
  note: string | null;
  sources: readonly Citation[];
}

export interface RenderNote {
  /** The espeak-ng phoneme mnemonic used to render this letter's clip. */
  espeak: string | null;
  /** The IPA espeak actually produces for it. */
  produces: string | null;
  /** True when `produces` differs from the reconstructed `ipa`. */
  diverges: boolean;
  /** How it differs. Required whenever `diverges` is true. */
  note: string | null;
}

export interface SoundValue {
  /** Vocabulary class id (append-only; matches vocab.json and AlphabetEntry.id). */
  letterClass: number;
  /** Canonical class name from the tokenizer ('s1', 'ḥ'). */
  letter: string;
  /** Human display form ('s¹'). */
  display: string;
  kind: 'letter' | 'special';
  /** Reconstructed consonant value, IPA. Null when none is recorded. */
  ipa: string | null;
  certainty: 'secure' | 'contested' | 'unknown';
  gloss: string | null;
  /** The usual modern Arabic reflex — a reflex, NOT the value of this letter. */
  arabicReflex: string | null;
  alternatives: readonly Alternative[];
  note: string | null;
  sources: readonly Citation[];
  render: RenderNote | null;
  /** Null until an epigrapher signs the entry off. Null on every entry today. */
  reviewedBy: string | null;
  /** Whether a rendered clip exists. The clip PAYLOAD lives in the sibling artifact, */
  /** which is loaded on demand — a caller can decide whether to offer a play control */
  /** without pulling 47 KB of audio onto a screen that never plays any. */
  hasClip: boolean;
  /** The support vowels the clip is framed by, e.g. 'a_a'. Null when there is none. */
  clipFrame: string | null;
}

export interface Reference {
  key: string;
  author: string;
  title: string;
  kind: string;
  detail: string | null;
  year: number | null;
  pages: string | null;
  /** False when the work is listed but was not consulted for any value here. */
  consulted: boolean;
}

export const PHONOLOGY_SOURCE = {
  vocabSha256: '427aa136e90c4f58ef0c93a46eedd035177d92cb11b242c44dcc47ad864faae0',
  valuesSha256: 'a38f1a373d89db52d704a03714e9a4814a56114661131ed9901f7782b12c600f',
  secureCount: 21,
  contestedCount: 7,
  unrecordedCount: 2,
  reviewedCount: 0,
} as const;

export const PHONOLOGY: readonly SoundValue[] = [
  {
    letterClass: 0, letter: 'ʾ', display: 'ʾ', kind: 'letter',
    ipa: 'ʔ', certainty: 'secure', gloss: 'glottal stop',
    arabicReflex: 'ʔ (hamza)',
    alternatives: [],
    note: 'A full letter of the alphabet, not punctuation. Transliterated U+02BE, never an apostrophe.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: '?', produces: 'ʔ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 1, letter: 'ʿ', display: 'ʿ', kind: 'letter',
    ipa: 'ʕ', certainty: 'secure', gloss: 'voiced pharyngeal fricative',
    arabicReflex: 'ʕ (ʿayn)',
    alternatives: [],
    note: 'Transliterated U+02BF. Retained unchanged in every modern Arabic variety, so the modern sound is a good guide here.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'A', produces: 'ʕ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 2, letter: 'b', display: 'b', kind: 'letter',
    ipa: 'b', certainty: 'secure', gloss: 'voiced bilabial stop',
    arabicReflex: 'b',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'b', produces: 'b', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 3, letter: 'd', display: 'd', kind: 'letter',
    ipa: 'd', certainty: 'secure', gloss: 'voiced alveolar stop',
    arabicReflex: 'd',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'd', produces: 'd', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 4, letter: 'ḏ', display: 'ḏ', kind: 'letter',
    ipa: 'ð', certainty: 'secure', gloss: 'voiced interdental fricative',
    arabicReflex: 'ð (MSA); retained as [ð] in the Bedouin dialects of the Ḥismā, merged to [d]/[z] in most urban varieties',
    alternatives: [],
    note: 'One of the three interdentals. The local Bedouin varieties keep them, which makes them a better living guide than Modern Standard Arabic pronunciation as usually taught.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'D', produces: 'ð', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 5, letter: 'ḍ', display: 'ḍ', kind: 'letter',
    ipa: 'ɮˤ', certainty: 'contested', gloss: 'pharyngealized voiced lateral fricative',
    arabicReflex: 'dˤ (MSA ḍād)',
    alternatives: [{ ipa: 'dˤ', note: 'The modern Arabic value. Assuming it here imports centuries of sound change.', sources: [{ key: 'modern_dialects', locus: null }] }, { ipa: 'd͡ɮˤ', note: 'A lateral affricate, if the emphatic series was affricated.', sources: [{ key: 'proto_semitic', locus: null }] }, { ipa: 'ɬʼ', note: 'A voiceless lateral ejective — the counterpart under the glottalic reconstruction of the emphatics, matching the ejective alternatives listed for ṣ, ṭ and ẓ.', sources: [{ key: 'proto_semitic', locus: null }] }],
    note: 'The emphatic counterpart of s². That is the argument: if s² is a lateral fricative then so is ḍ, and the two entries stand or fall together. Earlier revisions rested this on Sībawayh\'s description of the Arabic ḍād as produced along the side of the tongue — a source marked `consulted: false` in this repository, describing a different language several centuries later, and carrying the only articulatory claim in the file. It is still worth knowing, but it is not the support. No modern Arabic variety preserves this sound, so modern speech cannot settle it.',
    sources: [{ key: 'steiner1977', locus: null }, { key: 'macdonald2004', locus: null }],
    render: { espeak: 'dH', produces: 'dˤ', diverges: true, note: 'espeak has no lateral emphatic; it renders the modern Arabic [dˤ] instead. The synthesized sound is the modern reflex, not the reconstructed value.' },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 6, letter: 'f', display: 'f', kind: 'letter',
    ipa: 'f', certainty: 'secure', gloss: 'voiceless labiodental fricative',
    arabicReflex: 'f',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'f', produces: 'f', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 7, letter: 'g', display: 'g', kind: 'letter',
    ipa: 'ɡ', certainty: 'secure', gloss: 'voiced velar stop',
    arabicReflex: 'ɡ in the Bedouin varieties and Egyptian; d͡ʒ in MSA and most of the Levant',
    alternatives: [],
    note: 'The affrication to [d͡ʒ] is a later Arabic development; [ɡ] is the value reconstructed for this period.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'g', produces: 'ɡ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 8, letter: 'ġ', display: 'ġ', kind: 'letter',
    ipa: 'ɣ', certainty: 'secure', gloss: 'voiced uvular fricative',
    arabicReflex: 'ɣ (ġayn)',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'gH', produces: 'ɣ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 9, letter: 'h', display: 'h', kind: 'letter',
    ipa: 'h', certainty: 'secure', gloss: 'voiceless glottal fricative',
    arabicReflex: 'h',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'h', produces: 'h', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 10, letter: 'ḥ', display: 'ḥ', kind: 'letter',
    ipa: 'ħ', certainty: 'secure', gloss: 'voiceless pharyngeal fricative',
    arabicReflex: 'ħ (ḥāʾ)',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'H', produces: 'ħ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 11, letter: 'ḫ', display: 'ḫ', kind: 'letter',
    ipa: 'χ', certainty: 'secure', gloss: 'voiceless uvular fricative',
    arabicReflex: 'χ (ḫāʾ)',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'X', produces: 'χ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 12, letter: 'k', display: 'k', kind: 'letter',
    ipa: 'k', certainty: 'secure', gloss: 'voiceless velar stop',
    arabicReflex: 'k',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'k', produces: 'k', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 13, letter: 'l', display: 'l', kind: 'letter',
    ipa: 'l', certainty: 'secure', gloss: 'alveolar lateral approximant',
    arabicReflex: 'l',
    alternatives: [],
    note: 'The most frequent letter in the corpus at 16% of all letter tokens, almost entirely the preposition l- \'by\'.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'l', produces: 'l', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 14, letter: 'm', display: 'm', kind: 'letter',
    ipa: 'm', certainty: 'secure', gloss: 'bilabial nasal',
    arabicReflex: 'm',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'm', produces: 'm', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 15, letter: 'n', display: 'n', kind: 'letter',
    ipa: 'n', certainty: 'secure', gloss: 'alveolar nasal',
    arabicReflex: 'n',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'n', produces: 'n', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 16, letter: 'q', display: 'q', kind: 'letter',
    ipa: 'q', certainty: 'contested', gloss: 'voiceless uvular stop',
    arabicReflex: 'ɡ in the Bedouin varieties of the Ḥismā and northwest Arabia; q in MSA',
    alternatives: [{ ipa: 'ɡ', note: 'The reflex in the Bedouin dialects spoken where these inscriptions are, and a plausible older value.', sources: [{ key: 'modern_dialects', locus: null }] }, { ipa: 'kʼ', note: 'An ejective, if the emphatic series was glottalic rather than pharyngealized.', sources: [{ key: 'proto_semitic', locus: null }] }],
    note: 'The Bedouin dialects of this region have [ɡ] for qāf, but that is an INNOVATION in Arabic rather than a retention, so it is not evidence for an ancient [ɡ] — reading it back onto a first-century stone is exactly what this project warns against elsewhere. The interesting form of the argument runs the other way: a deglottalized ejective [kʼ] plausibly yields [ɡ], so the modern reflex is better evidence for an earlier ejective than for an old [ɡ], which makes this entry consistent with the emphatic series rather than an exception to it.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'q', produces: 'q', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 17, letter: 'r', display: 'r', kind: 'letter',
    ipa: 'r', certainty: 'secure', gloss: 'alveolar trill',
    arabicReflex: 'r',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'r', produces: 'r', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 18, letter: 's1', display: 's¹', kind: 'letter',
    ipa: 's', certainty: 'contested', gloss: 'voiceless alveolar fricative',
    arabicReflex: 's (the Arabic sīn continues this series)',
    alternatives: [{ ipa: 'ʃ', note: 'The older reading, taken from the Hebrew reflex. Held here to be an innovation in Hebrew rather than the ancestral value.', sources: [{ key: 'macdonald2000', locus: null }, { key: 'macdonald2004', locus: null }] }],
    note: 'Corresponds to Proto-Semitic *š — but *š is a CONVENTIONAL LABEL for this correspondence set, not a transcription of its sound, and the value generally reconstructed for the set is [s]. Earlier revisions of this file read the label as a value and entered [ʃ]: the same category error the numeral notation s¹/s²/s³ exists to prevent. Three things agree on [s]: the Arabic sīn continues this series; Greek transcription of ANA names writes it with σ; and the corpus\'s commonest names read Salm and Saʿd, not Shalm and Shaʿd.',
    sources: [{ key: 'macdonald2000', locus: null }, { key: 'macdonald2004', locus: null }, { key: 'aljallad2017', locus: null }],
    render: { espeak: 's', produces: 's', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 19, letter: 's2', display: 's²', kind: 'letter',
    ipa: 'ɬ', certainty: 'contested', gloss: 'voiceless alveolar lateral fricative',
    arabicReflex: 'No reflex. Merged away in every modern Arabic variety, generally into the sīn or šīn series.',
    alternatives: [{ ipa: 'ʃ', note: 'If the lateral reconstruction is wrong.', sources: [{ key: 'macdonald2000', locus: null }, { key: 'macdonald2004', locus: null }] }, { ipa: 't͡ɬ', note: 'A lateral affricate, on the same reasoning as the emphatics.', sources: [{ key: 'steiner1977', locus: null }] }],
    note: 'Corresponds to Proto-Semitic *ś. The lateral reconstruction is Steiner\'s and is mainstream — it explains the Arabic ش / Hebrew שׂ correspondence. Macdonald is cited here for the s¹/s²/s³ NOTATION, and declines to assign a phonetic value at all, so he is not the authority for the lateral reading. This remains the least secure sound in the inventory and the one no living Arabic speaker can demonstrate. It couples to s¹: [s]/[ɬ] is a coherent two-sibilant system in a way [ʃ]/[ɬ] is not.',
    sources: [{ key: 'steiner1977', locus: null }, { key: 'macdonald2000', locus: null }, { key: 'macdonald2004', locus: null }],
    render: { espeak: 'l#', produces: 'ɬ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 20, letter: 'ṣ', display: 'ṣ', kind: 'letter',
    ipa: 'sˤ', certainty: 'contested', gloss: 'pharyngealized voiceless alveolar fricative',
    arabicReflex: 'sˤ (MSA ṣād)',
    alternatives: [{ ipa: 't͡sˤ', note: 'An affricate, on the general reconstruction of the Semitic emphatics.', sources: [{ key: 'proto_semitic', locus: null }] }, { ipa: 'sʼ', note: 'An ejective, if emphasis was glottalic at this date.', sources: [{ key: 'proto_semitic', locus: null }] }],
    note: 'Whether the emphatic series was pharyngealized (as in modern Arabic) or glottalic/ejective (as reconstructed for Proto-Semitic) is not settled for this period. The whole series ṣ ṭ ḍ ẓ carries the same question. Pharyngealized is primary on grounds of DATE: these are first-millennium-CE texts, and glottalic emphasis is the Proto-Semitic state several millennia earlier.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 's[', produces: 's̪', diverges: true, note: 'espeak\'s Arabic ṣād is dental [s̪] with no pharyngealization. The synthesized sound is noticeably weaker than the intended emphatic.' },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 21, letter: 't', display: 't', kind: 'letter',
    ipa: 't', certainty: 'secure', gloss: 'voiceless alveolar stop',
    arabicReflex: 't',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 't', produces: 't', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 22, letter: 'ṭ', display: 'ṭ', kind: 'letter',
    ipa: 'tˤ', certainty: 'contested', gloss: 'pharyngealized voiceless alveolar stop',
    arabicReflex: 'tˤ (MSA ṭāʾ)',
    alternatives: [{ ipa: 'tʼ', note: 'An ejective, on the Proto-Semitic reconstruction of the emphatics.', sources: [{ key: 'proto_semitic', locus: null }] }],
    note: 'Same open question as ṣ: pharyngealized or glottalic. Pharyngealized is primary on grounds of DATE: these are first-millennium-CE texts, and glottalic emphasis is the Proto-Semitic state several millennia earlier.',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 't[', produces: 't̪', diverges: true, note: 'espeak\'s Arabic ṭāʾ is dental [t̪] with no pharyngealization.' },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 23, letter: 'ṯ', display: 'ṯ', kind: 'letter',
    ipa: 'θ', certainty: 'secure', gloss: 'voiceless interdental fricative',
    arabicReflex: 'θ (MSA); retained in the Bedouin dialects of the region, merged to [t]/[s] in most urban varieties',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'T', produces: 'θ', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 24, letter: 'w', display: 'w', kind: 'letter',
    ipa: 'w', certainty: 'secure', gloss: 'labial-velar approximant',
    arabicReflex: 'w',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'w', produces: 'w', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 25, letter: 'y', display: 'y', kind: 'letter',
    ipa: 'j', certainty: 'secure', gloss: 'palatal approximant',
    arabicReflex: 'j (yāʾ)',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'j', produces: 'j', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 26, letter: 'z', display: 'z', kind: 'letter',
    ipa: 'z', certainty: 'secure', gloss: 'voiced alveolar fricative',
    arabicReflex: 'z',
    alternatives: [],
    note: null,
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'z', produces: 'z', diverges: false, note: null },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 27, letter: 'ẓ', display: 'ẓ', kind: 'letter',
    ipa: 'ðˤ', certainty: 'contested', gloss: 'pharyngealized voiced interdental fricative',
    arabicReflex: 'ðˤ (MSA ẓāʾ); retained in the Bedouin varieties',
    alternatives: [{ ipa: 'θʼ', note: 'A voiceless ejective, matching ṣ → [sʼ] and ṭ → [tʼ] under the glottalic reconstruction.', sources: [{ key: 'proto_semitic', locus: null }] }],
    note: 'The rarest letter in the corpus, about 0.14% of letter tokens; any per-class metric on it will be noisy. An earlier revision listed the ejective alternative as [ðʼ] — a voiced ejective, which is typologically near-unattested. Under the glottalic reconstruction Proto-Semitic emphatics are voiceless, so the rival is [θʼ].',
    sources: [{ key: 'macdonald2004', locus: null }],
    render: { espeak: 'Z', produces: 'ð', diverges: true, note: 'espeak renders this as plain [ð]; the pharyngealization is lost entirely.' },
    reviewedBy: null,
    hasClip: true, clipFrame: 'a_a',
  },
  {
    letterClass: 28, letter: 'illegible', display: 'illegible', kind: 'special',
    ipa: null, certainty: 'unknown', gloss: null,
    arabicReflex: null,
    alternatives: [],
    note: 'Not a sound. A stretch the editor could not read, of unknown length. It has no pronunciation and must never be given one.',
    sources: [],
    render: null,
    reviewedBy: null,
    hasClip: false, clipFrame: null,
  },
  {
    letterClass: 29, letter: 'unknown', display: 'unknown', kind: 'special',
    ipa: null, certainty: 'unknown', gloss: null,
    arabicReflex: null,
    alternatives: [],
    note: 'Not a sound. A character the tokenizer could not classify. It has no pronunciation and must never be given one.',
    sources: [],
    render: null,
    reviewedBy: null,
    hasClip: false, clipFrame: null,
  },
];

export const REFERENCES: readonly Reference[] = [
  { key: 'aljallad2015', author: 'Al-Jallad, A.', title: 'An Outline of the Grammar of the Safaitic Inscriptions', kind: 'book', detail: 'Studies in Semitic Languages and Linguistics 80. Leiden: Brill', year: 2015, pages: null, consulted: true },
  { key: 'aljallad2017', author: 'Al-Jallad, A.', title: 'Graeco-Arabica I: the southern Levant', kind: 'chapter', detail: 'In A. Al-Jallad (ed.), Arabic in Context. Leiden: Brill', year: 2017, pages: null, consulted: true },
  { key: 'arabic_morphology', author: '(general reference)', title: 'Standard Arabic nominal morphology (the wazn / pattern system)', kind: 'general', detail: 'Textbook triliteral nominal patterns: faʿl, faʿal, faʿīl, fuʿayl, fāʿil, ʾafʿal, faʿlān, faʿlat', year: null, pages: null, consulted: true },
  { key: 'harding1971', author: 'Harding, G. Lankester', title: 'An Index and Concordance of Pre-Islamic Arabian Names and Inscriptions', kind: 'book', detail: 'Near and Middle East Series 8. Toronto: University of Toronto Press', year: 1971, pages: null, consulted: false },
  { key: 'king1990', author: 'King, G.M.H.', title: 'Early North Arabian Thamudic E. A preliminary description based on a new corpus of inscriptions from the Ḥismā desert of southern Jordan and published material', kind: 'thesis', detail: 'Ph.D thesis, School of Oriental and African Studies, University of London. Unpublished.', year: 1990, pages: null, consulted: false },
  { key: 'macdonald2000', author: 'Macdonald, M.C.A.', title: 'Reflections on the linguistic map of pre-Islamic Arabia', kind: 'article', detail: 'Arabian Archaeology and Epigraphy 11', year: 2000, pages: '28-79', consulted: true },
  { key: 'macdonald2004', author: 'Macdonald, M.C.A.', title: 'Ancient North Arabian', kind: 'chapter', detail: 'In R.D. Woodard (ed.), The Cambridge Encyclopedia of the World\'s Ancient Languages. Cambridge University Press', year: 2004, pages: null, consulted: true },
  { key: 'modern_dialects', author: '(general reference)', title: 'Northwest Arabian Bedouin dialect features', kind: 'general', detail: 'The dialects of the Ḥismā and northwest Arabia: interdentals retained, [g] for qāf', year: null, pages: null, consulted: true },
  { key: 'proto_semitic', author: '(general reference)', title: 'Comparative-Semitic reconstruction of the emphatic series', kind: 'general', detail: 'The standard reconstruction of Proto-Semitic emphatics as glottalic (ejective) rather than pharyngealized', year: null, pages: null, consulted: true },
  { key: 'sibawayh', author: 'Sībawayh', title: 'al-Kitāb', kind: 'primary', detail: '8th-century description of Arabic articulation', year: null, pages: null, consulted: false },
  { key: 'steiner1977', author: 'Steiner, R.C.', title: 'The Case for Fricative-Laterals in Proto-Semitic', kind: 'book', detail: 'American Oriental Series 59. New Haven: American Oriental Society', year: 1977, pages: null, consulted: true },
];
